/* eslint-disable max-lines, max-nested-callbacks */
// @ts-check

import {Component} from 'react';

import Breadcrumbs from './Breadcrumbs';
import Dropdown from './Dropdown';
import {Files} from './Files';
import Search from './Search';

/**
 * @typedef NavigatorProps
 * @prop {string[]} path
 * @prop {Record<string, any>} objects
 * @prop {Set<number>} selection
 * @prop {(id: string, type: string, path: string[], isTop: boolean) => void} [onNavigate]
 * @prop {(ids: string[]) => void} onSetSelection
 * @prop {(ids: string[]) => void} onAddSelection
 * @prop {(ids: string[]) => void} onRemoveSelection
 * @prop {(path: string[], id: string) => void} onPathChange
 * @prop {(id: string, type: string, name: string) => void} onSaveName
 * @prop {(id: string, type: string, target?: string) => void} [onChoose]
 * @prop {(id: string, name: string) => void} onEdit
 * @prop {(e: import("react").MouseEvent<HTMLButtonElement>, id?: string) => void} onFileMenu
 * @prop {() => void} onCancelName
 * @prop {string} toplevel
 * @prop {string} search
 * @prop {(search: string) => void} onSearch
 * @prop {string} mode
 * @prop {string} limit
 * @prop {boolean} own
 *
 * @typedef NavigatorState
 * @prop {string} displayed
 * @prop {boolean} async
 * @prop {number} selected
 * @prop {string | null} child
 * @prop {string | null} parent
 * @prop {string | null} parentPos
 * @prop {string | null} currentPos
 * @prop {string | null} childPos
 * @prop {string[]} [path]
 */

/** @extends Component<NavigatorProps, NavigatorState> */
class Navigator extends Component {
    /** @param {NavigatorProps} props */
    constructor(props) {
        super(props);

        const displayed = props.path[props.path.length - 1];
        const parent = props.path.slice(0, -1) ? props.path.slice(0, -1)[0] : null;
        this.state = {
            displayed,
            parent,
            async: false,
            selected: props.selection.size,
            child: null,
            parentPos: 'parent',
            currentPos: 'current',
            childPos: null,
        };
    }

    /** @param {NavigatorProps} nextProps */
    UNSAFE_componentWillReceiveProps(nextProps) {
        const parent = nextProps.path ? nextProps.path[nextProps.path.length - 1] : null;
        const displayed = nextProps.path[nextProps.path.length - 1];

        const metadata = nextProps.objects[displayed];

        // Path is shorter, animate up.
        if (nextProps.path.length < this.props.path.length) {
            this.setState(
                {
                    parent,
                    parentPos: 'parent',
                    path: nextProps.path,
                    selected: 0,
                },
                () => {
                    setTimeout(() => {
                        this.setState(
                            {
                                parentPos: 'current',
                                currentPos: 'child',
                            },
                            () => {
                                setTimeout(() => {
                                    this.setState(
                                        {
                                            displayed,
                                            currentPos: null,
                                        },
                                        () => {
                                            this.setState({
                                                parent,
                                                parentPos: 'parent',
                                                currentPos: 'current',
                                            });
                                        }
                                    );
                                }, 500);
                            }
                        );
                    }, 10);
                }
            );
            // Path is longer, animate down.
        } else if (nextProps.path.length > this.props.path.length) {
            this.setState(
                {
                    child: displayed,
                    childPos: 'child',
                    path: nextProps.path,
                    async: metadata.async,
                    selected: 0,
                },
                () => {
                    setTimeout(() => {
                        this.setState(
                            {
                                currentPos: 'parent',
                                childPos: 'current',
                            },
                            () => {
                                setTimeout(() => {
                                    this.setState(
                                        {
                                            displayed,
                                            currentPos: null,
                                        },
                                        () => {
                                            this.setState({
                                                child: null,
                                                currentPos: 'current',
                                                childPos: null,
                                            });
                                        }
                                    );
                                }, 500);
                            }
                        );
                    }, 10);
                }
            );
        } else {
            this.setState({
                displayed,
                parent,
                child: null,
                async: false,
                selected: nextProps.selection.size,
                parentPos: 'parent',
                currentPos: 'current',
                childPos: null,
            });
        }
    }

    /**
     * Navigate up `n` levels of the path to `id`. Calls onNavigate to fetch children,
     * onSetSelection to clear selection and onPathChange to update UI.
     *
     * @param  {number} n - Number of levels to go up.
     * @param  {string} id - Id to navigate to.
     * @param  {string} type - Object type `folder` / `toplevel`
     */
    onUp(n, id, type) {
        const path = this.props.path.slice(0, -1 * n);
        const isTop = n === this.props.path.length;

        if (this.props.onNavigate) {
            this.props.onNavigate(id, type, path, isTop);
        }

        this.props.onSetSelection([]);

        if (!isTop) {
            this.props.onPathChange(path, id);
        }
    }

    /**
     * Navigate to child object `id`. Calls onNavigate to fetch children,
     * onSetSelection to clear selection and onPathChange to update UI.
     *
     * @param  {string} id - Id to navigate to.
     */
    onDown(id) {
        const path = this.props.path.concat([id]);
        const type = this.props.objects[id].type;

        if (this.props.onNavigate) {
            this.props.onNavigate(id, type, path, false);
        }

        this.props.onSetSelection([]);
        this.props.onPathChange(path, id);
    }

    /**
     * @param {string} id
     * @param {string} value
     */
    onToggle(id, value) {
        if (value) {
            this.props.onAddSelection([id]);
        } else {
            this.props.onRemoveSelection([id]);
        }
    }

    /** @param {boolean} value */
    onToggleAll(value) {
        const displayed = this.props.path[this.props.path.length - 1];
        const metadata = this.props.objects[displayed];
        const children = metadata.children ? metadata.children : [];

        if (value) {
            this.props.onAddSelection(children);
        } else {
            this.props.onRemoveSelection(children);
        }
    }

    /**
     * @param {string} id
     * @param {any} name
     */
    onSaveName(id, name) {
        const type = this.props.objects[id].type;

        this.props.onSaveName(id, type, name);
    }

    /** @param {string} id */
    onChoose(id) {
        const type = this.props.objects[id].type;

        this.props.onChoose?.(id, type);
    }

    render() {
        return (
            <div>
                <div className='header'>
                    <Breadcrumbs
                        toplevel={this.props.toplevel}
                        objects={this.props.objects}
                        path={this.props.path}
                        onUp={(n, id, type) => this.onUp(n, id, type)}
                    />
                    {this.props.selection.size ? (
                        <span className='count'>{this.props.selection.size}</span>
                    ) : null}
                    <Search value={this.props.search} onChange={this.props.onSearch} />
                </div>
                <Files
                    mode={this.props.mode}
                    displayed={this.state.parent}
                    objects={this.props.objects}
                    limit={this.props.limit}
                    async={this.state.async}
                    selection={new Set([])}
                    search={''}
                    own={this.props.own}
                    position={this.state.parentPos}
                />
                <Files
                    mode={this.props.mode}
                    displayed={this.state.child}
                    objects={this.props.objects}
                    limit={this.props.limit}
                    async={this.state.async}
                    selection={new Set([])}
                    search={''}
                    own={this.props.own}
                    position={this.state.childPos}
                />
                <Files
                    mode={this.props.mode}
                    displayed={this.state.displayed}
                    objects={this.props.objects}
                    limit={this.props.limit}
                    async={this.state.async}
                    selection={this.props.selection}
                    search={this.props.search}
                    position={this.state.currentPos}
                    own={this.props.own}
                    onEdit={this.props.onEdit}
                    onSaveName={(id, name) => this.onSaveName(id, name)}
                    onCancelName={this.props.onCancelName}
                    onDown={(id) => this.onDown(id)}
                    onFileMenu={this.props.onFileMenu}
                    onChoose={(id) => this.onChoose(id)}
                    onToggle={(id, value) => this.onToggle(id, value)}
                    onToggleAll={(value) => this.onToggleAll(value)}
                />
                <Dropdown />
            </div>
        );
    }
}

export default Navigator;
